import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "Can I switch plans anytime?",
    answer: "Yes! You can upgrade or downgrade your membership plan at any time. When you upgrade, you'll get immediate access to all new features. If you downgrade, the change will take effect at the start of your next billing cycle, and you'll keep your current benefits until then."
  },
  {
    question: "Is there a free trial available?",
    answer: "We offer a 7-day free trial for the Premium plan so you can explore all features risk-free. No credit card required to start. You can also access our Free Lessons page anytime to get a feel for Mr. Augustine's teaching style before committing."
  },
  {
    question: "What devices can I use to access the platform?",
    answer: "Our platform works seamlessly on all devices — desktop computers, laptops, tablets (iPad, Android tablets), and smartphones (iOS and Android). You can switch between devices anytime, and your progress syncs automatically across all of them."
  },
  {
    question: "Is there a money-back guarantee?",
    answer: "Absolutely! We offer a 30-day money-back guarantee on all paid plans. If you're not completely satisfied with your membership for any reason, just contact us within 30 days of purchase for a full refund — no questions asked."
  },
  {
    question: "Can I cancel my membership anytime?",
    answer: "Yes, you can cancel anytime with no penalties or cancellation fees. Your access will continue until the end of your current billing period. You can reactivate your membership at any time and pick up right where you left off."
  },
  {
    question: "Do I get free updates and new content?",
    answer: "Yes! All members receive free access to new lessons, practice tests, and features as they're released. We continuously update our content library based on current curriculum standards and student feedback. Your membership includes all future updates at no extra cost."
  },
  {
    question: "How many students can use one account?",
    answer: "Each membership is designed for one student. However, our Family plan (coming soon) will allow up to 4 students to use separate profiles under one subscription. For now, if you have multiple children, we recommend separate accounts to track individual progress accurately."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, Mastercard, American Express, Discover), debit cards, and PayPal. All payments are processed securely through industry-standard encryption. We never store your full payment information on our servers."
  },
  {
    question: "Can I download content for offline use?",
    answer: "Premium and Elite members can download worksheets, practice tests, and lesson notes as PDFs for offline study. Video lessons can be accessed offline through our mobile app (iOS and Android) by downloading them in advance. Basic members have online-only access."
  },
  {
    question: "How do I access my account after purchase?",
    answer: "Immediately after purchase, you'll receive a welcome email with your login credentials and a link to access your dashboard. Simply log in with your email and password, and you'll have instant access to all your membership benefits. If you don't see the email, check your spam folder or contact support."
  },
  {
    question: "What if I need technical support?",
    answer: "All members have access to email support. Premium members get priority support with responses within 6 hours, while Elite members have access to live chat and phone support during business hours (9am–6pm EST, Monday–Friday). We're here to help you succeed!"
  },
  {
    question: "Are the practice tests aligned with state standards?",
    answer: "Yes! All our practice tests and lessons are carefully aligned with Common Core State Standards and state-specific requirements for NY, TX, OH, VA, and PA. Our Regents exams are based on actual NYS curriculum, and we update content regularly to reflect any changes in testing formats."
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-white to-emerald-50/30">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <i className="ri-question-answer-line text-3xl text-emerald-600"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about memberships, access, and getting started with Mr. Augustine's Math Academy
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border-2 border-gray-100 overflow-hidden hover:border-emerald-200 transition-all duration-300"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-emerald-50/50 transition-colors duration-200 cursor-pointer"
                aria-expanded={openIndex === index}
              >
                <span className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-emerald-600 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              {/* Answer */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5 pt-2">
                  <p className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center p-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl">
          <h3 className="text-2xl font-bold text-white mb-3">
            Still have questions?
          </h3>
          <p className="text-emerald-50 mb-6 text-lg">
            We're here to help! Reach out to our support team anytime.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-emerald-600 font-semibold rounded-full hover:bg-emerald-50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line text-xl"></i>
            Contact Support
          </a>
        </div>
      </div>
    </section>
  );
}
